import Vue from 'vue'
import Vuex from 'vuex'
import { FeathersVuex } from '../feathers-client'
import { timestamp } from '../utils'
import { NOTE_LIST_FIELDS } from '../lib/notes'
import { timed, rows, report } from '../lib/timing'

import authPlugin from './store.auth'

Vue.use(Vuex)
Vue.use(FeathersVuex)

const requireModule = require.context('./services', false, /.js$/)
const servicePlugins = requireModule.keys()
  .map(modulePath => requireModule(modulePath).default)

export default new Vuex.Store({
  plugins: [
    ...servicePlugins,
    authPlugin
  ],
  state: {
    connected: false,
    sync: false,
    loading: true,
    openSidebar: false,
    selectedFolder: {},
    selectedNoteId: null
  },
  getters: {
    connected: state => state.connected
  },
  mutations: {
    toggleSidebar(state, bool) {
      if (bool === undefined) {
        state.openSidebar = !state.openSidebar
      } else {
        state.openSidebar = bool
      }
    },
    selectFolder: (state, folder) => {
      state.selectedFolder = folder
    },
    setSelectedNoteId: (state, noteId) => {
      state.selectedNoteId = noteId
    },
    setConnected: (state, bool) => {
      state.connected = bool
    },
    setLoading: (state, bool) => {
      state.loading = bool
    },
    setSync: (state, bool) => {
      state.sync = bool
    }
  },
  actions: {
    // Two phases, not one queue.
    //
    // This used to await four finds in a row and only clear the loading flag
    // after the last one, so the app was blocked on the slowest thing it would
    // ever fetch. Worse, one try/catch wrapped all four: when notes/find failed
    // the catch swallowed it and the list rendered empty with no error anywhere.
    //
    // Phase one is only what the shell needs to draw -- folders and note
    // metadata, a few tens of KB. The app is usable once that lands. Tasks and
    // events continue in the background and each failure is now reported on its
    // own rather than cancelling the steps behind it.
    async sync({ commit, dispatch }) {
      const started = performance.now()

      const notesParams = { query: { $select: NOTE_LIST_FIELDS } }

      try {
        const [folders, notes] = await Promise.all([
          timed('folders', () => dispatch('folders/find')),
          timed('notes.list', () => dispatch('notes/find', notesParams))
        ])
        rows('folders', (folders || []).length)
        rows('notes.list', (notes || []).length)
      } catch (e) {
        console.log(timestamp() + ' error sync (shell)', e)
      } finally {
        // usable now: the body of a note is fetched when it is opened
        commit('setLoading', false)
      }

      const rest = await Promise.allSettled([
        timed('tasks', () => dispatch('tasks/find')),
        timed('events', () => dispatch('events/find'))
      ])
      rest.forEach(r => {
        if (r.status === 'rejected') console.log(timestamp() + ' error sync (background)', r.reason)
      })

      console.log(timestamp() + ' done sync')
      report({ total: Math.round(performance.now() - started) })
    },
    // A reconnect only has to catch up on what changed while the socket was
    // down, so it asks for records newer than the most recent updatedAt already
    // in the store rather than refetching everything. A tab left open for a day
    // reconnects many times; each one used to re-download the whole account.
    //
    // Records deleted while disconnected are not covered by this -- the delete
    // event arrives over the socket when the app is connected, and a cold start
    // fetches the full set. That is the trade: a reconnect is cheap, a cold
    // start is authoritative.
    reconnectSync({ commit, dispatch, getters }) {
      commit('setSync', true)

      const since = service => {
        const list = getters[`${service}/list`] || []
        const latest = list.reduce((max, item) => {
          const t = item.updatedAt || item.createdAt
          return t && t > max ? t : max
        }, '')
        return latest ? { updatedAt: { $gt: latest } } : {}
      }

      Promise.allSettled([
        dispatch('tasks/continueQueue'),
        dispatch('events/continueQueue'),
        dispatch('notes/continueQueue'),
        dispatch('folders/continueQueue'),
        dispatch('folders/find', { query: since('folders') }),
        dispatch('notes/find', { query: { ...since('notes'), $select: NOTE_LIST_FIELDS } }),
        dispatch('tasks/find', { query: since('tasks') }),
        dispatch('events/find', { query: since('events') })
      ]).finally(() => {
        console.log('done sync')
        commit('setSync', false)
      })
    }
  }
})
